"use client";

import { Menu } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import NavLink from "../NavLink";
import Icon from "@/components/Icons/Icon";
import IconPhone from "@/components/Icons/IconPhone";
import IconPlus from "@/components/Icons/IconPlus";
import { mobileNavigationLinks, phoneNumber, socialMediaIcons } from "@/utils/constants";
import PhoneLink from "@/components/common/links/PhoneLink";

const NavMobileMenu = () => {
  const pathname = usePathname();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <>
      <div className="flex space-x-2 xl:hidden">
        <button onClick={toggleSidebar}>
          <Icon
            icon={<Menu color="#4F5E71" />}
            width={14}
            height={14}
            className="bg-blue hover:cursor-pointer hover:bg-[#345A8C33]"
          />
        </button>
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 xl:hidden"
          onClick={closeSidebar}
          onKeyDown={(e) => e.key === "Enter" && closeSidebar()}
          role="button"
          tabIndex={0}
        />
      )}

      <div
        className={`fixed inset-0 z-[99999] flex h-full flex-col overflow-y-scroll bg-white shadow-lg ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out xl:hidden`}
      >
        <div className="flex items-center justify-between p-5">
          <div className="relative w-32">
            <Link href="/" onClick={closeSidebar}>
              <Image
                src="/assets/images/albacars-logo.png"
                alt="logo"
                width={729}
                height={223}
                className="object-cover object-center"
                sizes="140px"
              />
            </Link>
          </div>
          <button onClick={closeSidebar} className="rounded-2xl bg-blue p-4 text-xl hover:bg-[#345A8C33]">
            <IconPlus className="rotate-45" />
          </button>
        </div>

        <div className="flex flex-1 flex-col justify-center space-y-4 px-5">
          {mobileNavigationLinks.map((link, idx) => (
            <NavLink
              key={`mob-${link.href}-${idx}`}
              variant="navbar"
              href={link.href}
              isActive={pathname === link.href}
              onClick={closeSidebar}
              className="rounded-xl p-4 !text-base font-medium"
              noPrefetch={link?.noPrefetch ?? false}
            >
              {link.name}
            </NavLink>
          ))}
        </div>

        <div className="mt-auto p-5">
          <div className="mx-auto rounded-2xl bg-blue p-4">
            <div>
              <h6 className="mb-2 font-inter text-sm font-medium text-[#292D3480] text-opacity-50">Contacts</h6>
              <div className="flex items-center">
                <PhoneLink
                  phoneNumber={phoneNumber}
                  linkClassNames="flex items-center hover:underline"
                  buttonClassNames="flex items-center hover:underline"
                >
                  <IconPhone />
                  <span className="ml-2 font-inter text-base">{phoneNumber}</span>
                </PhoneLink>
              </div>
            </div>
            <div className="mt-5">
              <h6 className="mb-2 font-inter text-sm font-medium text-[#292D3480] text-opacity-50">Social Networks</h6>

              <div className="flex space-x-3">
                {socialMediaIcons.map((iconItem) => (
                  <Link href={iconItem.url} target="_blank" key={iconItem.key} onClick={closeSidebar}>
                    <Icon
                      icon={<iconItem.default />}
                      width={10}
                      height={10}
                      className="cursor-pointer rounded-full bg-black invert"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavMobileMenu;
